import { inject, Injectable, Type } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, finalize, first, map, Observable } from 'rxjs';
import {
  CommunityFocus,
  CommunityInviteOrDeclineRequest,
  ConnectionRequestAction,
  ProjectInviteOrDeclineRequest,
  DefaultService as RestApiService,
  UserRole,
} from '../../../swagger-api';
import { ChatEngineNotificationChannelService } from '../../chat/interfaces/chat-engine/chat-engine-notification-channel.service';
import { ManageProfileService } from '../../profile/manage-profile.service';
import { InitiatorRole } from '../../profile/profile.enum';
import { ConversationalNotificationComponent } from '../conversational-notification/conversational-notification.component';
import {
  NotificationAction,
  NotificationDataPayload,
  NotificationEvent,
  NotificationMessagePayload,
} from '../interfaces';
import { NotificationContext } from '../notification.context';
import { doctorPatientLinkInitiatorError, getPropertyGuarded } from '../notification.helpers';
import { EventNotificationsStrategy } from '../strategy/event-notifications.interface';
import { EventNotificationsContext, UnsupportedNotificationStrategy } from '../strategy/event-notifications.strategy';
import { NOTIFICATION_ACTION_HANDLERS } from '../strategy/notification-action-handlers';
import {
  NOTIFICATION_DEFAULT_COMPONENT_TYPE,
  NOTIFICATION_INVERTED_COMPONENT_TYPES,
} from '../strategy/notifications.types';
import { STRATEGY_TYPES } from '../strategy/strategy.types';

export interface NotificationViewModel {
  notificationDataPayload: NotificationDataPayload;
  componentType: Type<NotificationContext>;
}

@Injectable({
  providedIn: 'root',
})
export class EventNotificationsService {
  public get notificationChannelConnectionStateChanged(): Observable<boolean> {
    return this.notificationEngineService.notificationChannelConnectionStateChanged;
  }

  public get notificationMessageAdded(): Observable<NotificationViewModel> {
    return this.notificationEngineService.notificationMessageAdded.pipe(
      map((event: NotificationEvent) => this.generateNotificationViewModel(event)),
    );
  }

  public get notificationChannelInitialMessagesLoaded(): Observable<NotificationViewModel[]> {
    return this.notificationEngineService.notificationChannelInitialMessagesLoaded.pipe(
      map((events: NotificationEvent[]) =>
        events.map((event: NotificationEvent) => this.generateNotificationViewModel(event)),
      ),
    );
  }

  public get notificationMessageRemoved(): Observable<string> {
    return this.notificationEngineService.notificationMessageRemoved;
  }

  public get notificationChannelCleared(): Observable<void> {
    return this.notificationEngineService.notificationChannelCleared;
  }

  private eventNotificationsContext: EventNotificationsContext;

  private apiService = inject(RestApiService);
  private notificationEngineService = inject(ChatEngineNotificationChannelService);
  private translateService = inject(TranslateService);
  private manageProfileService = inject(ManageProfileService);

  public deleteNotification({ message_sid }: NotificationAction): void {
    this.notificationEngineService.removeNotification(message_sid);
  }

  public confirmConnection({ message_sid, payload }: NotificationAction): void {
    this.deleteNotification({ message_sid } as NotificationAction);

    this.performApiCall(
      this.apiService.restSocialProfileConnectionsPut(
        { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Verified },
        payload,
      ),
    );
  }

  public rejectConnection({ message_sid, payload }: NotificationAction): void {
    this.deleteNotification({ message_sid } as NotificationAction);
    this.performApiCall(
      this.apiService.restSocialProfileConnectionsPut(
        { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Rejected },
        payload,
      ),
    );
  }

  public acceptDoctorPatientLink({ message_sid, initiatorRole, doctorPatientLinkId }: NotificationAction): void {
    this.deleteNotification({ message_sid } as NotificationAction);
    if (initiatorRole === InitiatorRole.patient) {
      this.performApiCall(
        this.apiService.restMedicalPatientLinkPut(
          { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Verified },
          doctorPatientLinkId,
        ),
      );
    } else if (initiatorRole === InitiatorRole.doctor) {
      this.performApiCall(
        this.apiService.restMedicalDoctorLinkPut(
          { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Verified },
          doctorPatientLinkId,
        ),
      );
    } else {
      doctorPatientLinkInitiatorError(initiatorRole);
    }
  }

  public rejectDoctorPatientLink({ message_sid, initiatorRole, doctorPatientLinkId }: NotificationAction): void {
    this.deleteNotification({ message_sid } as NotificationAction);
    if (initiatorRole === InitiatorRole.patient) {
      this.performApiCall(
        this.apiService.restMedicalPatientLinkPut(
          { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Rejected },
          doctorPatientLinkId,
        ),
      );
    }

    if (initiatorRole === InitiatorRole.doctor) {
      this.performApiCall(
        this.apiService.restMedicalDoctorLinkPut(
          { connection_status: ConnectionRequestAction.ConnectionStatusEnum.Rejected },
          doctorPatientLinkId,
        ),
      );
    }
  }

  public approveProjectParticipation({ message_sid, projectId, participantId }: NotificationAction): void {
    this.deleteNotification({ message_sid } as NotificationAction);
    this.performApiCall(
      this.apiService.restSocialProjectMembersPut(
        { invitation_status: ProjectInviteOrDeclineRequest.InvitationStatusEnum.Approved },
        projectId,
        participantId,
      ),
    );
  }

  public rejectProjectParticipation({ message_sid, projectId, participantId }: NotificationAction): void {
    this.deleteNotification({ message_sid } as NotificationAction);

    this.performApiCall(
      this.apiService.restSocialProjectMembersPut(
        { invitation_status: ProjectInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        projectId,
        participantId,
      ),
    );
  }

  public acceptCommunityParticipation({ message_sid, communityId, participantId }: NotificationAction): void {
    this.deleteNotification({ message_sid } as NotificationAction);
    this.performApiCall(
      this.apiService.restSocialCommunityMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Approved },
        communityId,
        participantId,
      ),
    );
  }

  public rejectCommunityParticipation({ message_sid, communityId, participantId }: NotificationAction): void {
    this.deleteNotification({ message_sid } as NotificationAction);
    this.performApiCall(
      this.apiService.restSocialCommunityMembersPut(
        { invitation_status: CommunityInviteOrDeclineRequest.InvitationStatusEnum.Rejected },
        communityId,
        participantId,
      ),
    );
  }

  public revertAccountReactivation({ message_sid, initiatorEmail }: NotificationAction): void {
    this.manageProfileService
      .revertAccountReactivationPetition(initiatorEmail)
      .pipe(
        first(),
        finalize(() => this.deleteNotification({ message_sid } as NotificationAction)),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  public declineAccountReactivation({ message_sid, initiatorEmail }: NotificationAction): void {
    this.manageProfileService
      .confirmDeclineAccountReactivationPetition(initiatorEmail)
      .pipe(
        first(),
        catchError(() => {
          this.deleteNotification({ message_sid } as NotificationAction);
          return EMPTY;
        }),
      )
      .subscribe((declineConfirmed: boolean) => {
        if (declineConfirmed) this.deleteNotification({ message_sid } as NotificationAction);
      });
  }

  private performApiCall<T>(apiCall$: Observable<T>): void {
    apiCall$
      .pipe(
        first(),
        catchError(() => EMPTY),
      )
      .subscribe();
  }

  private generateNotificationViewModel(notification: NotificationEvent): NotificationViewModel {
    const strategy: EventNotificationsStrategy = this.strategyForMessageClass(notification.message_class);

    this.eventNotificationsContext = new EventNotificationsContext(strategy);

    const componentType: object = this.componentForMessageClass(notification.message_class);

    const notificationDataPayload: NotificationDataPayload = {
      message: this.translateNotificationMessagePayload(notification),
      dateCreated: notification.message_date_created,
      entityId: getPropertyGuarded(notification.message_data, 'initiator_account_id'),
      messageSid: notification.message_sid,
      projectId: getPropertyGuarded(notification.message_data, 'project_id'),
      communityId: getPropertyGuarded(notification.message_data, 'community_id'),
      participantId:
        getPropertyGuarded(notification.message_data, 'participant_account_id') ||
        getPropertyGuarded(notification.message_data, 'leader_account_id'),
      routerUrl: this.eventNotificationsContext.doCreateRouterUrl(notification),
      fileId: getPropertyGuarded(notification.message_data, 'file_descriptor_id'),
      initiatorRole: getPropertyGuarded(notification.message_data, 'initiator_role') as UserRole,
      doctorPatientLinkId: getPropertyGuarded(notification.message_data, 'doctor_patient_link_id'),
      initiatorEmail: getPropertyGuarded(notification.message_data, 'initiator_email'),
      communityFocus: getPropertyGuarded(notification.message_data, 'community_focus') as CommunityFocus,
    };

    if (componentType === ConversationalNotificationComponent) {
      notificationDataPayload.actions = NOTIFICATION_ACTION_HANDLERS[notification.message_class];
    }

    return {
      notificationDataPayload: notificationDataPayload,
      componentType: componentType as Type<NotificationContext>,
    };
  }

  private translateNotificationMessagePayload(notification: NotificationEvent): NotificationMessagePayload[] {
    const message: NotificationMessagePayload[] =
      this.eventNotificationsContext.doCreateNotificationMessagePayload(notification);

    message.map((payload: NotificationMessagePayload) => {
      if (!payload.href && payload.text) {
        payload.text = this.translateService.instant(payload.text) as string;
      }
    });
    return message;
  }

  private strategyForMessageClass(messageClass: string): EventNotificationsStrategy {
    if (messageClass in STRATEGY_TYPES) {
      return STRATEGY_TYPES[messageClass];
    } else {
      return new UnsupportedNotificationStrategy();
    }
  }

  private componentForMessageClass(messageClass: string): object {
    if (messageClass in NOTIFICATION_INVERTED_COMPONENT_TYPES) {
      return NOTIFICATION_INVERTED_COMPONENT_TYPES[messageClass];
    } else {
      return NOTIFICATION_DEFAULT_COMPONENT_TYPE;
    }
  }
}
